import { useEffect, useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { StoreSummaryDto } from '@bhops/shared';
import { storesApi, type UpdateStoreBody } from '@/api/stores';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Input, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';

interface Props {
  open: boolean;
  onClose: () => void;
  store: StoreSummaryDto;
}

export function StoreEditModal({ open, onClose, store }: Props) {
  const toast = useToast();
  const qc = useQueryClient();
  const [form, setForm] = useState<UpdateStoreBody>(() => dtoToForm(store));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setForm(dtoToForm(store));
      setError(null);
    }
  }, [open, store]);

  const update = useMutation({
    mutationFn: () => storesApi.update(store.id, form),
    onSuccess: (s) => {
      qc.invalidateQueries({ queryKey: ['stores'] });
      toast.success(`${s.name} 매장 정보를 저장했습니다.`, '저장 완료');
      onClose();
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    update.mutate();
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${store.name} 매장 편집`}
      width={500}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button type="submit" form="store-edit-form" disabled={update.isPending}>
            {update.isPending ? '저장 중…' : '저장'}
          </Button>
        </>
      }
    >
      <form id="store-edit-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
          <Field label="매장명" htmlFor="se-name" required>
            <Input
              id="se-name"
              value={form.name ?? ''}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              required
            />
          </Field>
          <Field label="지점명" htmlFor="se-branch">
            <Input
              id="se-branch"
              value={form.branchName ?? ''}
              onChange={(e) => setForm({ ...form, branchName: e.target.value || null })}
            />
          </Field>
        </div>

        <Field label="주소" htmlFor="se-addr">
          <Input
            id="se-addr"
            value={form.addressLine1 ?? ''}
            onChange={(e) => setForm({ ...form, addressLine1: e.target.value || null })}
          />
        </Field>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <Field label="도시" htmlFor="se-city">
            <Input
              id="se-city"
              value={form.city ?? ''}
              onChange={(e) => setForm({ ...form, city: e.target.value || null })}
            />
          </Field>
          <Field label="지역" htmlFor="se-region">
            <Input
              id="se-region"
              value={form.region ?? ''}
              onChange={(e) => setForm({ ...form, region: e.target.value || null })}
            />
          </Field>
          <Field label="국가" htmlFor="se-country">
            <Input
              id="se-country"
              value={form.country ?? 'KR'}
              maxLength={2}
              onChange={(e) => setForm({ ...form, country: e.target.value.toUpperCase() })}
              style={{ fontFamily: 'var(--font-mono)' }}
            />
          </Field>
        </div>

        <Field label="활성 여부" htmlFor="se-active">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
            <input
              id="se-active"
              type="checkbox"
              checked={form.active ?? true}
              onChange={(e) => setForm({ ...form, active: e.target.checked })}
            />
            운영 중인 매장
          </label>
        </Field>

        <Field label="메모" htmlFor="se-notes">
          <Textarea
            id="se-notes"
            value={form.notes ?? ''}
            onChange={(e) => setForm({ ...form, notes: e.target.value || null })}
            rows={3}
          />
        </Field>
      </form>
    </Modal>
  );
}

function dtoToForm(s: StoreSummaryDto): UpdateStoreBody {
  return {
    name: s.name,
    branchName: s.branchName,
    addressLine1: s.addressLine1,
    city: s.city,
    region: s.region,
    country: s.country,
    active: s.active,
  };
}
